<template>
  <div class="pou" role="status">
    <span :class="['pou-mark', `pou-mark--${tone}`]" aria-hidden="true">
      <i :class="markIcon"></i>
    </span>
    <component :is="tag" class="pou-title">{{ title }}</component>
    <div class="pou-text"><slot></slot></div>
    <div v-if="$slots.actions" :class="['pou-actions', { 'pou-actions--lg': actionsSize === 'lg' }]">
      <slot name="actions"></slot>
    </div>
  </div>
</template>

<script setup>
/**
 * L'ÉCRAN D'ISSUE D'UN PARCOURS PUBLIC : ce que le visiteur voit quand il n'y
 * a plus rien à remplir — parce que c'est fait, parce que c'était déjà fait,
 * ou parce que le lien ne vaut plus rien.
 *
 * Les trois disent la même chose dans la même forme : une pastille, un titre
 * au passé, une phrase qui dit ce qui se passe maintenant, au plus une action
 * secondaire. Seul le TON change — et il ne change que la couleur de la
 * pastille et son dessin, jamais le titre, qui reste dans la couleur du
 * texte : un titre vert sur fond vert (l'ancien « ✅ Devis signé avec
 * succès ! ») se lit moins bien qu'un titre noir, et un écran entier teinté
 * en rouge fait passer un lien périmé pour une panne.
 *
 * Pas d'emoji dans les titres (P11) : la coche dessinée se lit pareil sur
 * tous les systèmes.
 *
 * `role="status"` : l'écran remplace le contenu sans navigation, un lecteur
 * d'écran doit l'annoncer.
 *
 * Remplace `PublicSuccess`, qui ne connaissait que le cas « ça a marché » :
 * la signature de devis a trois issues, l'émargement et l'inscription en
 * auront autant.
 */
import { computed } from 'vue'

const props = defineProps({
  title: { type: String, required: true },
  /**
   * `success` : c'est fait. `warning` : c'était déjà fait, ou il n'y a rien à
   * faire ici. `neutral` : le lien est mort — ce n'est ni une réussite ni une
   * faute du visiteur.
   */
  tone: {
    type: String,
    default: 'success',
    validator: (value) => ['success', 'warning', 'neutral'].includes(value)
  },
  /** Remplace le dessin par défaut du ton. */
  icon: { type: String, default: null },
  tag: {
    type: String,
    default: 'h1',
    validator: (value) => ['h1', 'h2'].includes(value)
  },
  /**
   * `lg` : les actions parlent en 16 px et 52 px (`--public-submit-h`) au lieu
   * des 1,05 rem de `.btn-lg`, et s'empilent pleine largeur sous 768 px.
   * Réservé aux écrans d'issue où l'action EST l'écran (rien d'autre à faire
   * ici) — /contrat-annuel, /desinscription-formateur et le renvoi Global
   * Prévention de l'espace formateur. Par défaut (`null`), les actions gardent la taille du bouton
   * telle que l'appelant l'a posée.
   */
  actionsSize: {
    type: String,
    default: null,
    validator: (value) => !value || value === 'lg'
  }
})

const DEFAULT_ICONS = {
  success: 'fas fa-check',
  warning: 'fas fa-clock',
  neutral: 'fas fa-link-slash'
}

const markIcon = computed(() => props.icon || DEFAULT_ICONS[props.tone])
</script>

<style scoped>
.pou {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-3);
  padding: var(--spacing-4) var(--spacing-2) var(--spacing-2);
  text-align: center;
}

.pou-mark {
  width: 56px;
  height: 56px;
  border-radius: var(--radius-full);
  font-size: var(--font-size-2xl);
  display: flex;
  align-items: center;
  justify-content: center;
}

.pou-mark--success {
  background: var(--color-success-lightest);
  color: var(--color-success);
}

.pou-mark--warning {
  background: var(--color-warning-lightest);
  color: var(--color-warning);
}

.pou-mark--neutral {
  background: var(--color-bg-tertiary);
  color: var(--color-text-tertiary);
}

.pou-title {
  margin: var(--spacing-2) 0 0;
  font-size: var(--font-size-2xl);
  line-height: 1.3;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  text-wrap: balance;
}

/* Un `<div>` et non un `<p>` : une issue porte parfois deux phrases qui ne
   vont pas ensemble (« votre copie est partie à… » puis « il reste à nous
   dire… »), et un `<p>` ne peut pas en contenir un autre. */
.pou-text {
  max-width: 520px;
  font-size: var(--font-size-base);
  line-height: 1.6;
  color: var(--color-text-secondary);
}

.pou-text :deep(p) {
  margin: 0;
}

.pou-text :deep(p + p) {
  margin-top: var(--spacing-3);
}

.pou-actions {
  margin-top: var(--spacing-3);
  display: flex;
  gap: var(--spacing-2);
  justify-content: center;
  flex-wrap: wrap;
}

/* `actions-size="lg"` : `.btn-lg` pose 1,05 rem, ces écrans d'issue parlent
   en 16 px et en 52 px (`--public-submit-h`). */
.pou-actions--lg :deep(.btn) {
  min-height: var(--public-submit-h);
  font-size: var(--font-size-base);
}

@media (max-width: 768px) {
  .pou-actions--lg {
    flex-direction: column;
    align-self: stretch;
  }
}
</style>
